/**
 * The client record's storage rows (docs/43 §3.2, §3.3; crossmarc/steward-app#2699 slice 1).
 * CONTRACT ONLY: no writer, route or reader consumes these yet. The five types are registered
 * in `STEWARD_ADVISOR_FIRM_TYPE_DEFS` (`clientRecordTypeDefs.ts`), so the pack seed writes
 * their CTYPE rows, but nothing writes an instance. They are the shapes slices 2 to 6 build against.
 *
 *   - `client-answers`: every client- and staff-written answer, each `{ value, via, by, at }`,
 *     one `_v` for the whole row ({@link ClientAnswersRowSchema});
 *   - `client-connected`: what a bank link brought in, written only by the intake writer
 *     ({@link ClientConnectedRowSchema}), at today's caps (10 institutions, 50 accounts, 100
 *     holdings: `intakeSnapshot.ts`);
 *   - `client-documents`: references to uploaded files, written only by the writer
 *     ({@link ClientDocumentsRowSchema}), at today's cap
 *     ({@link INTAKE_STATEMENT_MAX_PER_DRAFT}, 20).
 *
 * ## Provenance (§3.3)
 *
 * `via` is the route that wrote the value, `at` is SERVER time, and **`by` is the server-side
 * caller's userId, taken from the verified session. It is never a field the browser sends**, on
 * any route: a body carrying `via`, `by` or `at` is not a thing the writer reads. (These are
 * schemas of the STORED row, so they cannot enforce that; the slice-2 route must.) `via`, `by`
 * and `at` change only when the value changes (§3.3), which is writer logic, not schema.
 *
 * ## Shape
 *
 * Scalars are `answers[unifiedId]`. A list (cash accounts, children, ...) is `lists[listId]`, an
 * array of `{ id, fields }`: `id` is the minted item id (`QuestionItemIdSchema`, the shape of
 * a snapshot `rowId`) and `fields[itemFieldId]` is an answer with its OWN provenance, so a
 * staff-entered balance beside a client-entered institution keeps both marks.
 *
 * The SSN and every `protected-identity` question are never here: the row schema refuses the
 * key ({@link checkAnswerKey}). An unanswered question has NO key; clearing deletes it.
 *
 * ## Size (§3.1)
 *
 * {@link worstCaseClientAnswersRow} builds the largest row the schema admits; the size test
 * (`clientAnswers.test.ts`) measures it against DynamoDB's 409,600-byte item limit.
 */
import { z } from 'zod';
import { IntakeDraftSnapshotSchema } from './intakeSnapshot.js';
import { ULID_PATTERN } from './intakeMessage.js';
import { IntakeDraftDocumentsSchema } from './intakeStatements.js';
import { QuestionItemIdSchema } from './questionCatalogue.js';
import {
  INFO_REQUEST_ID_ITEM_QUESTIONS, UNIFIED_QUESTIONS, checkAnswerKey, unifiedQuestionById, unifiedValidator, type UnifiedQuestion,
} from './unifiedQuestions.js';

/** The four row types' codes. Registered in `STEWARD_ADVISOR_FIRM_TYPE_DEFS` (`clientRecordTypeDefs.ts`). */
export const CLIENT_ANSWERS_TYPE_CODE = 'client-answers';
export const CLIENT_CONNECTED_TYPE_CODE = 'client-connected';
export const CLIENT_DOCUMENTS_TYPE_CODE = 'client-documents';
export const CLIENT_REQUESTS_TYPE_CODE = 'client-requests';

/** Which route wrote a value. `connected` is a Plaid-sourced value and is valid ONLY in a `client-connected` row (`via: 'connected'` there); `client-answers` and `client-requests` accept only the three people-routes. */
export const ANSWER_VIAS = ['snapshot', 'onboarding', 'staff'] as const;
export type AnswerVia = (typeof ANSWER_VIAS)[number];

/** A userId: a 26-character ULID (the shape of `ULID_PATTERN`). */
const ByUserId = z.string().regex(ULID_PATTERN);
/** Server time, `Z` form. `z.iso.datetime()` admits arbitrary fractional digits, so bound it. */
const At = z.iso.datetime().max(24);

/**
 * A record whose raw input has no own `__proto__` key. zod's record assigns parsed keys onto a
 * plain object, where a parsed `__proto__` is silently DROPPED rather than rejected, so a row
 * carrying one would validate and then lose the answer (found in review 2026-10-31; pinned by a
 * shared vector). The check runs on the RAW input, before the record parse. Every other unknown
 * key is refused by the row's `superRefine`.
 */
function safeRecord<V extends z.ZodType>(value: V) {
  return z.custom<unknown>((x) => typeof x !== 'object' || x === null || Array.isArray(x) || !Object.hasOwn(x, '__proto__'), 'reserved key')
    .pipe(z.record(z.string().max(80), value));
}

const AnswerValue = z.union([z.string(), z.boolean(), z.array(z.string())]);

export const ClientAnswerSchema = z.strictObject({
  value: AnswerValue,
  via: z.enum(ANSWER_VIAS),
  /** The server-side caller's userId. Never a browser field. */
  by: ByUserId,
  at: At,
});
export type ClientAnswer = z.infer<typeof ClientAnswerSchema>;

/** What a route may accept from a browser for one answer: the value ONLY. `via`, `by` and `at` are stamped server-side. */
export const ClientAnswerInputSchema = z.strictObject({ value: AnswerValue });
export type ClientAnswerInput = z.infer<typeof ClientAnswerInputSchema>;

export const ClientAnswerListItemSchema = z.strictObject({
  /** The minted item id (`QuestionItemIdSchema`). */
  id: QuestionItemIdSchema,
  /** Item field question id (`cashAccounts[].institution`) to its answer. */
  fields: safeRecord(ClientAnswerSchema),
});
export type ClientAnswerListItem = z.infer<typeof ClientAnswerListItemSchema>;

function issue(ctx: z.RefinementCtx, path: (string | number)[], message: string): void {
  ctx.addIssue({ code: 'custom', path, message });
}

function checkValue(ctx: z.RefinementCtx, path: (string | number)[], question: UnifiedQuestion, value: unknown): void {
  const v = unifiedValidator(question.id);
  if (!v || !v.safeParse(value).success) {
    issue(ctx, [...path, 'value'], `invalid value for ${question.id}`);
    return;
  }
  // An answer that is empty after trimming is a CLEAR, and a clear deletes the key (an
  // unanswered question has no key). So a STORED text or email answer is never empty. (A
  // string-list item may be: the request lists are parallel by index.)
  if ((question.valueType === 'text' || question.valueType === 'email') && typeof value === 'string' && value.trim() === '') {
    issue(ctx, [...path, 'value'], `an empty answer for ${question.id} is a clear: delete the key`);
  }
}

export const ClientAnswersRowSchema = z.strictObject({
  /** The one version of the row (§5.2). */
  _v: z.number().int().min(0),
  answers: safeRecord(ClientAnswerSchema),
  lists: safeRecord(z.array(ClientAnswerListItemSchema)),
}).superRefine((row, ctx) => {
  for (const [key, answer] of Object.entries(row.answers)) {
    const verdict = checkAnswerKey(key);
    // A scalar answer is filed under the bare question id, never an item key, and the request
    // lists belong to their own row.
    if (!verdict.ok || verdict.itemId !== undefined || verdict.question.listId !== undefined || verdict.question.row !== 'answers') {
      issue(ctx, ['answers', key], `not a scalar answer key: ${key}`);
      continue;
    }
    checkValue(ctx, ['answers', key], verdict.question, answer.value);
  }
  for (const [listId, items] of Object.entries(row.lists)) {
    const list = unifiedQuestionById(listId);
    if (!list || list.valueType !== 'list') { issue(ctx, ['lists', listId], `not a list question: ${listId}`); continue; }
    if (items.length > (list.maxItems ?? 0)) issue(ctx, ['lists', listId], `more than ${list.maxItems} items`);
    const seen = new Set<string>();
    items.forEach((item, i) => {
      if (seen.has(item.id)) issue(ctx, ['lists', listId, i, 'id'], 'duplicate item id');
      seen.add(item.id);
      // An item with no fields is not an item: clearing its last field deletes the item.
      if (Object.keys(item.fields).length === 0) issue(ctx, ['lists', listId, i, 'fields'], 'an item with no fields');
      for (const [fieldId, answer] of Object.entries(item.fields)) {
        const verdict = checkAnswerKey(`${fieldId}#${item.id}`);
        if (!verdict.ok || verdict.question.listId !== listId) {
          issue(ctx, ['lists', listId, i, 'fields', fieldId], `not a field of ${listId}: ${fieldId}`);
          continue;
        }
        checkValue(ctx, ['lists', listId, i, 'fields', fieldId], verdict.question, answer.value);
      }
    });
  }
});
export type ClientAnswersRow = z.infer<typeof ClientAnswersRowSchema>;

/**
 * `client-requests`: the onboarding form's two request lists (`requests.answerIds`, the requests
 * the client answered, and `requests.answerTexts`, the text typed for each), parallel by index.
 * Its own row and its own `_v` because the lists belong to a firm's request, answered later, and
 * would otherwise take ~150 KB of the answers row's budget. Provenance is per list, not per
 * item: the lists are written together by one route. NOT in docs/43's table, which allowed a
 * `requests` row "only if" needed: see {@link UnifiedQuestion.row}.
 */
export const ClientRequestsRowSchema = z.strictObject({
  _v: z.number().int().min(0),
  answerIds: ClientAnswerSchema,
  answerTexts: ClientAnswerSchema,
}).superRefine((row, ctx) => {
  checkValue(ctx, ['answerIds'], unifiedQuestionById('requests.answerIds')!, row.answerIds.value);
  checkValue(ctx, ['answerTexts'], unifiedQuestionById('requests.answerTexts')!, row.answerTexts.value);
  const ids = row.answerIds.value; const texts = row.answerTexts.value;
  if (!Array.isArray(ids) || !Array.isArray(texts) || ids.length !== texts.length) {
    issue(ctx, ['answerTexts'], 'answerIds and answerTexts must be parallel');
  }
});
export type ClientRequestsRow = z.infer<typeof ClientRequestsRowSchema>;

/**
 * `client-connected` (§3.2): the draft's `snapshot` attribute, accounts and holdings from bank
 * links, accumulated across links. Written ONLY by the intake writer on link-settle, so `via` is
 * always `connected` and `by` is the user whose link it was. Caps are `IntakeDraftSnapshotSchema`'s.
 */
export const ClientConnectedRowSchema = z.strictObject({
  _v: z.number().int().min(0),
  via: z.literal('connected'),
  by: ByUserId,
  at: At,
  snapshot: IntakeDraftSnapshotSchema,
});
export type ClientConnectedRow = z.infer<typeof ClientConnectedRowSchema>;

/**
 * `client-documents` (§3.2): references to uploaded files, written ONLY by the writer's
 * `document-attach`, never by a save. Capped by {@link IntakeDraftDocumentsSchema} (20 today, the
 * cap the draft's `documents` attribute already carries; docs/43 §3.2 said it had none, which
 * was a misreading: `INTAKE_STATEMENT_MAX_PER_DRAFT`, `intakeStatements.ts`).
 */
export const ClientDocumentsRowSchema = z.strictObject({
  _v: z.number().int().min(0),
  documents: IntakeDraftDocumentsSchema,
});
export type ClientDocumentsRow = z.infer<typeof ClientDocumentsRowSchema>;

// ---------------------------------------------------------------------------------------------
// Worst case (the size test's input)
// ---------------------------------------------------------------------------------------------

/** A character that is one UTF-16 unit and three UTF-8 bytes, so a field "at its cap" costs 3 bytes per unit. */
export const WORST_CASE_UNIT = '☃';
const WORST_USER_ID = '0'.repeat(26);
const WORST_AT = '2026-10-31T23:59:59.999Z';
const WORST_ITEM_ID = 'A'.repeat(32);

/** The longest email `z.email().max(254)` admits: ASCII only (the regex refuses anything else), so 1 byte per unit. 64 + 1 + 63 + 1 + 63 + 1 + 57 + 4 = 254. */
function worstEmail(cap: number): string {
  if (cap !== 254) throw new Error('worstEmail is written for the 254-unit email cap; recompute it');
  return `${'a'.repeat(64)}@${'b'.repeat(63)}.${'b'.repeat(63)}.${'b'.repeat(57)}.com`;
}

function worstValue(q: UnifiedQuestion): unknown {
  switch (q.valueType) {
    case 'text':
      if (q.maxLength === undefined) throw new Error(`uncapped text question: ${q.id} has no maxLength`);
      return WORST_CASE_UNIT.repeat(q.maxLength);
    case 'email':
      if (q.maxLength === undefined) throw new Error(`uncapped email question: ${q.id} has no maxLength`);
      return worstEmail(q.maxLength);
    case 'boolean': return false; // 'false' is the longest of the two literals
    case 'enum': return [...q.options!].sort((a, b) => b.length - a.length)[0]!;
    case 'multiEnum': return [...q.options!];
    case 'stringList':
      if (q.maxLength === undefined || q.maxItems === undefined) throw new Error(`uncapped string list: ${q.id} has no cap`);
      // An info-request-id list holds ASCII (64 at most), not the free-text worst case.
      return Array.from({ length: q.maxItems }, () => (INFO_REQUEST_ID_ITEM_QUESTIONS.has(q.id) ? 'a'.repeat(q.maxLength!) : WORST_CASE_UNIT.repeat(q.maxLength!)));
    case 'list': throw new Error(`${q.id} is a list container`);
  }
}

const worstAnswer = (q: UnifiedQuestion): ClientAnswer => ({
  value: worstValue(q) as ClientAnswer['value'], via: 'onboarding', by: WORST_USER_ID, at: WORST_AT,
});

/**
 * The largest `client-answers` row the schema admits: every scalar answered at its cap, every list
 * at its item cap with every item field answered at its cap, and provenance on every answer.
 * THROWS on an uncapped text question or list, so an unbounded field turns the size test red
 * rather than being counted as zero. `unifiedQuestions.test.ts`/`clientAnswers.test.ts` also
 * assert the schema ACCEPTS this row, so the caps counted are the caps enforced.
 */
export function worstCaseClientAnswersRow(questions: readonly UnifiedQuestion[] = UNIFIED_QUESTIONS): ClientAnswersRow {
  const answers: Record<string, ClientAnswer> = {};
  const lists: Record<string, ClientAnswerListItem[]> = {};
  for (const q of questions) {
    if (q.listId !== undefined || q.row !== 'answers') continue;
    if (q.valueType === 'list') {
      if (q.maxItems === undefined) throw new Error(`uncapped list: ${q.id} has no maxItems`);
      const fields = questions.filter((f) => f.listId === q.id);
      lists[q.id] = Array.from({ length: q.maxItems }, (_, i) => ({
        id: `${WORST_ITEM_ID.slice(0, 30)}${String(i).padStart(2, '0')}`,
        fields: Object.fromEntries(fields.map((f) => [f.id, worstAnswer(f)])),
      }));
    } else {
      answers[q.id] = worstAnswer(q);
    }
  }
  return { _v: 2_147_483_647, answers, lists };
}

/** The largest `client-requests` row: both lists full at their caps, with provenance on each. */
export function worstCaseClientRequestsRow(): ClientRequestsRow {
  return {
    _v: 2_147_483_647,
    answerIds: worstAnswer(unifiedQuestionById('requests.answerIds')!),
    answerTexts: worstAnswer(unifiedQuestionById('requests.answerTexts')!),
  };
}
